import { useState, useEffect, useRef, createContext, useContext } from "react";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CampaignIcon from "@mui/icons-material/Campaign";
import LoopIcon from "@mui/icons-material/Loop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ─── THEME FACTORY ──────────────────────────────────────────
   dark=true  → Blue Night  |  dark=false → Warm Light
─────────────────────────────────────────────────────────────── */
function getTheme(dark) {
  if (dark)
    return {
      dark: true,
      bg: "#0D1117",
      bgAlt: "#111827",
      bgCard: "#161D2B",
      bgLight: "#1A2235",
      bgDark: "#080C12",
      cream: "#F0EDE6",
      creamMid: "rgba(240,237,230,.65)",
      creamMuted: "rgba(240,237,230,.38)",
      ink: "#F0EDE6",
      inkMid: "rgba(240,237,230,.65)",
      inkMuted: "rgba(240,237,230,.38)",
      blue: "#3D7BF5",
      blueHov: "#2D6BE0",
      bluePale: "rgba(61,123,245,.12)",
      blueBorder: "rgba(61,123,245,.28)",
      rust: "#288ED2",
      rustHov: "#B04520",
      rustPale: "rgba(40,142,210,.14)",
      rustBorder: "rgba(40,142,210,.3)",
      forest: "#3A8C4E",
      forestPale: "rgba(58,140,78,.14)",
      gold: "#D4A843",
      border: "rgba(255,255,255,.08)",
      borderMid: "rgba(255,255,255,.13)",
      shadow: "rgba(0,0,0,.35)",
      shadowMd: "rgba(0,0,0,.55)",
      navBg: (sc) => (sc ? "rgba(13,17,23,.93)" : "rgba(13,17,23,.75)"),
      navLinkC: "rgba(240,237,230,.6)",
      navLinkHov: "#F0EDE6",
      gridLine: "rgba(61,123,245,.06)",
      statsGrad: "linear-gradient(90deg,#3D7BF5,#2558C8)",
      planFeatBg: "linear-gradient(145deg,#3D7BF5,#2050C0)",
      planFeatCta: "#fff",
      planFeatCtaTxt: "#3D7BF5",
      planFeatBadge: "#288ED2",
      tesiQuote: "rgba(40,142,210,.12)",
      footerBg: "#080C12",
      footerBrd: "rgba(255,255,255,.06)",
      footerTxt: "rgba(240,237,230,.28)",
      vsKadBg: "rgba(58,168,106,.06)",
      vsKadTxt: "#3AA86A",
    };
  return {
    dark: false,
    bg: "#FAFAF7",
    bgAlt: "#F4F1EB",
    bgCard: "#ffffff",
    bgLight: "#F0ECE4",
    bgDark: "#1C1A17",
    cream: "#1A1916",
    creamMid: "#4A4540",
    creamMuted: "#8C847A",
    ink: "#1A1916",
    inkMid: "#4A4540",
    inkMuted: "#8C847A",
    blue: "#3D7BF5",
    blueHov: "#2D6BE0",
    bluePale: "rgba(61,123,245,.08)",
    blueBorder: "rgba(61,123,245,.22)",
    rust: "#288ED2",
    rustHov: "#B04520",
    rustPale: "#FAF0EB",
    rustBorder: "#F0C4B0",
    forest: "#2D5016",
    forestPale: "#EEF5E9",
    gold: "#C49A2A",
    border: "#E2DDD6",
    borderMid: "#D0C9BF",
    shadow: "rgba(26,25,22,.08)",
    shadowMd: "rgba(26,25,22,.14)",
    navBg: (sc) => (sc ? "rgba(250,250,247,.96)" : "rgba(250,250,247,.82)"),
    navLinkC: "#4A4540",
    navLinkHov: "#288ED2",
    gridLine: "rgba(40,142,210,.04)",
    statsGrad: "linear-gradient(90deg,#288ED2,#B04520)",
    planFeatBg: "#288ED2",
    planFeatCta: "#fff",
    planFeatCtaTxt: "#288ED2",
    planFeatBadge: "#1A1916",
    tesiQuote: "#FAF0EB",
    footerBg: "#111009",
    footerBrd: "rgba(255,255,255,.06)",
    footerTxt: "rgba(250,240,235,.28)",
    vsKadBg: "rgba(45,80,22,.04)",
    vsKadTxt: "#2D5016",
  };
}

/* ─── CONTEXT ────────────────────────────────────────────────── */
const ThemeCtx = createContext(getTheme(true));
const useT = () => useContext(ThemeCtx);

/* ─── FONTS ─────────────────────────────────────────────────── */
const injectFonts = () => {
  if (document.getElementById("kadomi-fonts")) return;
  const link = document.createElement("link");
  link.id = "kadomi-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap";
  document.head.appendChild(link);
};

/* ─── GLOBAL STYLES ─────────────────────────────────────────── */
const injectGlobalStyles = () => {
  if (document.getElementById("kadomi-global")) return;
  const s = document.createElement("style");
  s.id = "kadomi-global";
  s.textContent = `
    *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
    html { scroll-behavior:smooth; }
    @keyframes fadeUp {
      from { opacity:0; transform:translateY(20px); }
      to   { opacity:1; transform:translateY(0);    }
    }
    @keyframes pulse {
      0%,100% { opacity:1; transform:scale(1);    }
      50%      { opacity:.3; transform:scale(1.8); }
    }
    @keyframes gridMove {
      from { transform:translateY(0);    }
      to   { transform:translateY(48px); }
    }
    .kd-fade-up  { animation:fadeUp .75s ease both; }
    .kd-delay-1  { animation-delay:.12s; }
    .kd-delay-2  { animation-delay:.24s; }
    .kd-delay-3  { animation-delay:.36s; }
    .kd-delay-4  { animation-delay:.48s; }
    .kd-reveal   { opacity:0; transform:translateY(22px);
                   transition:opacity .7s ease, transform .7s ease; }
    .kd-reveal.visible { opacity:1; transform:translateY(0); }
    .kd-d1 { transition-delay:.07s; }
    .kd-d2 { transition-delay:.17s; }
    .kd-d3 { transition-delay:.27s; }
    .kd-footer-link:hover { color:#F0EDE6 !important; }
  `;
  document.head.appendChild(s);
};

/* ─── HOOK ──────────────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) el.classList.add("visible");
      },
      { threshold: 0.08, rootMargin: "0px 0px -36px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  return isMobile;
}

/* ─── PRIMITIVES ─────────────────────────────────────────────── */
function Label({ children }) {
  const T = useT();
  return (
    <p
      style={{
        fontFamily: "'DM Mono',monospace",
        fontSize: ".68rem",
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: T.blue,
        marginBottom: ".875rem",
      }}
    >
      {children}
    </p>
  );
}

function Title({ children, center }) {
  const T = useT();
  return (
    <h2
      style={{
        fontFamily: "'Playfair Display',serif",
        fontSize: "clamp(1.9rem,3.8vw,2.85rem)",
        fontWeight: 700,
        lineHeight: 1.18,
        letterSpacing: "-.015em",
        color: T.cream,
        textAlign: center ? "center" : undefined,
      }}
    >
      {children}
    </h2>
  );
}

function Sub({ children, center }) {
  const T = useT();
  return (
    <p
      style={{
        marginTop: ".9rem",
        fontSize: "1rem",
        lineHeight: 1.78,
        maxWidth: 530,
        color: T.creamMid,
        textAlign: center ? "center" : undefined,
        marginInline: center ? "auto" : undefined,
      }}
    >
      {children}
    </p>
  );
}

const BtnPrimary = ({ children, href = "#contact" }) => {
  const T = useT();
  const [h, setH] = useState(false);
  return (
    <a
      href={href}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "inline-block",
        padding: ".875rem 2rem",
        borderRadius: 100,
        background: h ? T.rustHov : T.rust,
        color: "#fff",
        fontWeight: 600,
        fontSize: ".9375rem",
        textDecoration: "none",
        letterSpacing: ".01em",
        transform: h ? "translateY(-2px)" : "none",
        boxShadow: h
          ? "0 8px 28px rgba(40,142,210,.45)"
          : "0 2px 12px rgba(40,142,210,.3)",
        transition: "all .22s",
      }}
    >
      {children}
    </a>
  );
};

const BtnSecondary = ({ children, href = "#" }) => {
  const T = useT();
  const [h, setH] = useState(false);
  return (
    <a
      href={href}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "inline-block",
        padding: ".875rem 2rem",
        borderRadius: 100,
        background: h ? "rgba(240,237,230,.07)" : "transparent",
        border: `1.5px solid ${h ? T.borderMid : T.border}`,
        color: T.cream,
        fontWeight: 500,
        fontSize: ".9375rem",
        textDecoration: "none",
        transition: "all .22s",
      }}
    >
      {children}
    </a>
  );
};

/* ─── NAV ────────────────────────────────────────────────────── */
function ThemeToggle({ isDark, onToggle }) {
  const T = useT();
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onToggle}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        padding: ".4rem .875rem",
        borderRadius: 100,
        background: hov ? T.bgLight : T.bgCard,
        border: `1px solid ${hov ? T.borderMid : T.border}`,
        color: T.creamMid,
        fontSize: ".8rem",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all .2s",
        fontFamily: "'DM Mono',monospace",
      }}
    >
      <span style={{ fontSize: "1rem", lineHeight: 1 }}>
        {isDark ? "☀️" : "🌙"}
      </span>
      {isDark ? "Clair" : "Sombre"}
    </button>
  );
}

function Nav({ isDark, onToggle }) {
  const T = useT();
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: isMobile ? "1rem 1.5rem" : "1.125rem 3rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backdropFilter: "blur(20px) saturate(1.6)",
        background: T.navBg(scrolled),
        borderBottom: `1px solid ${scrolled ? T.border : "transparent"}`,
        boxShadow: scrolled
          ? T.dark
            ? "0 1px 32px rgba(0,0,0,.5)"
            : "0 1px 24px rgba(26,25,22,.1)"
          : "none",
        transition: "all .3s",
      }}
    >
      <a
        href="#"
        style={{
          fontFamily: "'Playfair Display',serif",
          fontWeight: 900,
          fontSize: isMobile ? "1.1rem" : "1.3rem",
          color: T.cream,
          textDecoration: "none",
          letterSpacing: "-.01em",
        }}
      >
        kadomi<span style={{ color: T.rust }}>.</span>
        <span style={{ color: T.creamMuted, fontWeight: 700 }}>shop</span>
      </a>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "1rem" : "1.75rem",
        }}
      >
        {!isMobile &&
          [
            ["#products", "Produits"],
            ["#network", "Network"],
            ["#pricing", "Tarifs"],
          ].map(([href, label]) => (
            <NavLink key={href} href={href} label={label} />
          ))}
        {!isMobile && <ThemeToggle isDark={isDark} onToggle={onToggle} />}
        <a
          href="#contact"
          style={{
            padding: isMobile ? ".45rem 1rem" : ".5rem 1.375rem",
            borderRadius: 100,
            background: T.rust,
            color: "#fff",
            fontWeight: 600,
            fontSize: isMobile ? ".8rem" : ".875rem",
            textDecoration: "none",
            boxShadow: "0 2px 10px rgba(40,142,210,.35)",
            transition: "background .2s",
          }}
        >
          {isMobile ? "Démo" : "Démo gratuite"}
        </a>
      </div>
    </nav>
  );
}

function NavLink({ href, label }) {
  const T = useT();
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        color: hov ? T.navLinkHov : T.navLinkC,
        fontSize: ".875rem",
        fontWeight: 500,
        textDecoration: "none",
        transition: "color .18s",
      }}
    >
      {label}
    </a>
  );
}

/* ─── HERO SVG ILLUSTRATION ──────────────────────────────────── */
function HeroIllustration() {
  return (
    <svg
      viewBox="0 0 780 580"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        width: "100%",
        height: "100%",
        maxHeight: 580,
        display: "block",
      }}
    >
      <defs>
        <clipPath id="hi-clip">
          <rect width="780" height="580" rx="20" />
        </clipPath>
        <linearGradient id="hi-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0D1117" />
          <stop offset="100%" stopColor="#0A1020" />
        </linearGradient>
        <linearGradient id="hi-c1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E2A3E" />
          <stop offset="100%" stopColor="#16202E" />
        </linearGradient>
        <linearGradient id="hi-c2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1A2D1E" />
          <stop offset="100%" stopColor="#132215" />
        </linearGradient>
        <linearGradient id="hi-c3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2A1E18" />
          <stop offset="100%" stopColor="#1E150F" />
        </linearGradient>
        <linearGradient id="hi-bblue" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3D7BF5" />
          <stop offset="100%" stopColor="#6B9FFA" />
        </linearGradient>
        <linearGradient id="hi-bgreen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3AA86A" />
          <stop offset="100%" stopColor="#5CC882" />
        </linearGradient>
        <linearGradient id="hi-brust" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#288ED2" />
          <stop offset="100%" stopColor="#E8765A" />
        </linearGradient>
        <linearGradient id="hi-ph" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1A2438" />
          <stop offset="100%" stopColor="#0F1824" />
        </linearGradient>
        <linearGradient id="hi-ovl" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0D1117" stopOpacity="0" />
          <stop offset="100%" stopColor="#0D1117" stopOpacity="0.8" />
        </linearGradient>
        <filter id="hi-sh" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow
            dx="0"
            dy="10"
            stdDeviation="16"
            floodColor="#000"
            floodOpacity="0.6"
          />
        </filter>
        <filter id="hi-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="35" />
        </filter>
      </defs>

      <g clipPath="url(#hi-clip)">
        {/* BG */}
        <rect width="780" height="580" fill="url(#hi-bg)" />

        {/* Grille */}
        <g opacity="0.055" stroke="#3D7BF5" strokeWidth="0.5">
          {[120, 200, 280, 360, 440, 520, 600, 680].map((x) => (
            <line key={x} x1={x} y1="0" x2={x} y2="580" />
          ))}
          {[100, 180, 260, 340, 420, 500].map((y) => (
            <line key={y} x1="0" y1={y} x2="780" y2={y} />
          ))}
        </g>

        {/* Halos */}
        <ellipse
          cx="580"
          cy="130"
          rx="300"
          ry="220"
          fill="#3D7BF5"
          opacity="0.09"
          filter="url(#hi-glow)"
        />
        <ellipse
          cx="140"
          cy="460"
          rx="200"
          ry="160"
          fill="#288ED2"
          opacity="0.08"
          filter="url(#hi-glow)"
        />

        {/* ── SCÈNE BOUTIQUE ── */}
        <rect x="110" y="80" width="390" height="340" fill="#111C2A" rx="2" />
        {/* étagères */}
        {[125, 200, 275].map((y) => (
          <rect
            key={y}
            x="115"
            y={y}
            width="380"
            height="7"
            rx="2"
            fill="#1E2E42"
          />
        ))}
        {/* produits — rangées */}
        {[[90, 95, 135, 170, 210, 250, 290, 330, 370, 410, 450]].map(
          (_, ri) => null
        )}
        {/* rangée 1 */}
        {[
          [130, "#288ED2", 0.8],
          [158, "#3D7BF5", 0.7],
          [182, "#3AA86A", 0.75],
          [208, "#D4A843", 0.7],
          [234, "#288ED2", 0.55],
          [262, "#3D7BF5", 0.6],
          [290, "#8B5CF6", 0.65],
          [318, "#3AA86A", 0.6],
          [346, "#E8765A", 0.7],
          [374, "#3D7BF5", 0.55],
          [402, "#D4A843", 0.65],
          [430, "#288ED2", 0.7],
          [458, "#3D7BF5", 0.6],
        ].map(([x, c, o], i) => (
          <rect
            key={i}
            x={x}
            y={88}
            width={20}
            height={28}
            rx="3"
            fill={c}
            opacity={o}
          />
        ))}
        {/* rangée 2 */}
        {[
          [130, "#3D7BF5", 0.6],
          [158, "#288ED2", 0.7],
          [184, "#3AA86A", 0.65],
          [212, "#D4A843", 0.6],
          [240, "#E8765A", 0.7],
          [268, "#8B5CF6", 0.6],
          [296, "#3D7BF5", 0.7],
          [324, "#288ED2", 0.55],
          [352, "#3AA86A", 0.6],
          [380, "#D4A843", 0.65],
          [408, "#288ED2", 0.7],
          [436, "#3D7BF5", 0.6],
          [464, "#E8765A", 0.55],
        ].map(([x, c, o], i) => (
          <rect
            key={i}
            x={x}
            y={162}
            width={20}
            height={28}
            rx="3"
            fill={c}
            opacity={o}
          />
        ))}
        {/* rangée 3 */}
        {[
          [130, "#D4A843", 0.65],
          [158, "#3D7BF5", 0.7],
          [184, "#288ED2", 0.6],
          [212, "#3AA86A", 0.7],
          [240, "#8B5CF6", 0.6],
          [268, "#E8765A", 0.65],
          [296, "#3D7BF5", 0.55],
          [324, "#288ED2", 0.7],
          [352, "#3AA86A", 0.6],
          [380, "#D4A843", 0.65],
          [408, "#288ED2", 0.55],
          [436, "#3D7BF5", 0.7],
          [464, "#E8765A", 0.6],
        ].map(([x, c, o], i) => (
          <rect
            key={i}
            x={x}
            y={237}
            width={20}
            height={28}
            rx="3"
            fill={c}
            opacity={o}
          />
        ))}

        {/* Comptoir */}
        <rect x="155" y="360" width="300" height="55" rx="6" fill="#152030" />
        <rect x="155" y="355" width="300" height="12" rx="4" fill="#1E3048" />

        {/* Laptop */}
        <rect
          x="200"
          y="320"
          width="190"
          height="110"
          rx="6"
          fill="#0D1824"
          stroke="#1E3048"
          strokeWidth="1.5"
        />
        <rect x="205" y="326" width="180" height="96" rx="4" fill="#0A1520" />
        {/* mini dashboard sur écran */}
        <text
          x="210"
          y="342"
          fontFamily="'DM Mono',monospace"
          fontSize="6.5"
          fill="#3D7BF5"
          fontWeight="500"
        >
          KADOMI DASHBOARD
        </text>
        <rect
          x="210"
          y="346"
          width="170"
          height="0.5"
          fill="#3D7BF5"
          opacity="0.2"
        />
        <text
          x="210"
          y="357"
          fontFamily="'DM Sans',sans-serif"
          fontSize="6"
          fill="#F0EDE6"
          opacity="0.5"
        >
          Ventes ce mois
        </text>
        <text
          x="374"
          y="357"
          fontFamily="'DM Mono',monospace"
          fontSize="6"
          fill="#3AA86A"
          fontWeight="500"
          textAnchor="end"
        >
          +18.4%
        </text>
        <rect x="210" y="360" width="80" height="3.5" rx="1.5" fill="#1E3048" />
        <rect
          x="210"
          y="360"
          width="58"
          height="3.5"
          rx="1.5"
          fill="url(#hi-bblue)"
        />
        <text
          x="210"
          y="373"
          fontFamily="'DM Sans',sans-serif"
          fontSize="6"
          fill="#F0EDE6"
          opacity="0.5"
        >
          Billets vendus
        </text>
        <rect x="210" y="376" width="80" height="3.5" rx="1.5" fill="#1E3048" />
        <rect
          x="210"
          y="376"
          width="48"
          height="3.5"
          rx="1.5"
          fill="url(#hi-bgreen)"
        />
        <text
          x="210"
          y="389"
          fontFamily="'DM Sans',sans-serif"
          fontSize="6"
          fill="#F0EDE6"
          opacity="0.5"
        >
          Commandes food
        </text>
        <rect x="210" y="392" width="80" height="3.5" rx="1.5" fill="#1E3048" />
        <rect
          x="210"
          y="392"
          width="36"
          height="3.5"
          rx="1.5"
          fill="url(#hi-brust)"
        />
        {/* Mini sparkline */}
        <polyline
          points="210,412 226,406 244,408 262,401 280,403 298,396 316,399 334,392 352,394 370,387"
          fill="none"
          stroke="#3D7BF5"
          strokeWidth="1.2"
          strokeLinecap="round"
        />

        {/* Clavier */}
        <rect x="188" y="415" width="214" height="10" rx="3" fill="#111E2E" />
        <rect x="262" y="417" width="80" height="6" rx="2.5" fill="#0A1520" />

        {/* Silhouette commerçant */}
        <ellipse
          cx="320"
          cy="310"
          rx="28"
          ry="36"
          fill="#0E1828"
          opacity="0.9"
        />
        <circle cx="320" cy="270" r="20" fill="#1A2640" opacity="0.95" />
        <rect
          x="300"
          y="295"
          width="40"
          height="50"
          rx="3"
          fill="#288ED2"
          opacity="0.3"
        />
        <path
          d="M343 318 Q360 332 375 350"
          fill="none"
          stroke="#1A2640"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <ellipse
          cx="320"
          cy="274"
          rx="16"
          ry="13"
          fill="#3D7BF5"
          opacity="0.05"
        />

        {/* overlay bas scène */}
        <rect x="110" y="340" width="390" height="80" fill="url(#hi-ovl)" />

        {/* ════════════════════════════════════════ */}
        {/* CARD 1 — Kadomi.ch — gauche flottante  */}
        {/* ════════════════════════════════════════ */}
        <g
          transform="translate(18, 42) rotate(-4, 130, 175)"
          filter="url(#hi-sh)"
        >
          <rect
            width="190"
            height="220"
            rx="14"
            fill="url(#hi-c1)"
            stroke="#3D7BF5"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect width="190" height="3" rx="2" fill="url(#hi-bblue)" />
          <rect
            x="12"
            y="14"
            width="70"
            height="15"
            rx="7"
            fill="#3D7BF5"
            fillOpacity="0.16"
            stroke="#3D7BF5"
            strokeWidth="0.5"
            strokeOpacity="0.5"
          />
          <text
            x="47"
            y="25.5"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#3D7BF5"
            fontWeight="500"
            textAnchor="middle"
          >
            kadomi.ch
          </text>
          <text
            x="12"
            y="48"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="12"
            fill="#F0EDE6"
            fontWeight="700"
          >
            Boutique en ligne
          </text>
          <text
            x="12"
            y="70"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="22"
            fill="#F0EDE6"
            fontWeight="900"
          >
            CHF 3'840
          </text>
          <text
            x="12"
            y="82"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#F0EDE6"
            opacity="0.45"
          >
            Ventes ce mois
          </text>
          <rect
            x="130"
            y="56"
            width="50"
            height="15"
            rx="7"
            fill="#3AA86A"
            fillOpacity="0.18"
            stroke="#3AA86A"
            strokeWidth="0.5"
            strokeOpacity="0.55"
          />
          <text
            x="155"
            y="67.5"
            fontFamily="'DM Mono',monospace"
            fontSize="8"
            fill="#3AA86A"
            fontWeight="500"
            textAnchor="middle"
          >
            ↑ 18%
          </text>
          <rect
            x="12"
            y="95"
            width="166"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <text
            x="12"
            y="108"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.35"
          >
            Ventes 7 derniers jours
          </text>
          {/* barres */}
          {[
            [14, 148, 36],
            [38, 135, 52],
            [62, 120, 68],
            [86, 128, 60],
            [110, 110, 78],
            [134, 98, 90],
            [158, 104, 84],
          ].map(([x, y, h], i) => (
            <rect
              key={i}
              x={x}
              y={y}
              width="17"
              height={h}
              rx="2.5"
              fill={i === 6 ? "url(#hi-bblue)" : "#3D7BF5"}
              opacity={0.25 + i * 0.1}
            />
          ))}
          {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
            <text
              key={i}
              x={22 + i * 24}
              y="197"
              fontFamily="'DM Mono',monospace"
              fontSize="7"
              fill={i === 6 ? "#3D7BF5" : "#F0EDE6"}
              opacity={i === 6 ? 1 : 0.28}
              textAnchor="middle"
            >
              {d}
            </text>
          ))}
          <rect
            x="12"
            y="205"
            width="166"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <text
            x="12"
            y="216"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#F0EDE6"
            opacity="0.3"
          >
            32 commandes · 0% commission
          </text>
        </g>

        {/* ════════════════════════════════════════ */}
        {/* CARD 2 — MyTicketing — haut centre     */}
        {/* ════════════════════════════════════════ */}
        <g
          transform="translate(285, 12) rotate(2, 185, 140)"
          filter="url(#hi-sh)"
        >
          <rect
            width="210"
            height="188"
            rx="14"
            fill="url(#hi-c2)"
            stroke="#3AA86A"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect width="210" height="3" rx="2" fill="url(#hi-bgreen)" />
          <rect
            x="12"
            y="14"
            width="92"
            height="15"
            rx="7"
            fill="#3AA86A"
            fillOpacity="0.14"
            stroke="#3AA86A"
            strokeWidth="0.5"
            strokeOpacity="0.5"
          />
          <text
            x="58"
            y="25.5"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#3AA86A"
            fontWeight="500"
            textAnchor="middle"
          >
            myticketing.ch
          </text>
          <text
            x="12"
            y="50"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="12"
            fill="#F0EDE6"
            fontWeight="700"
          >
            Concert — Lausanne
          </text>
          <rect
            x="12"
            y="56"
            width="80"
            height="17"
            rx="6"
            fill="#3AA86A"
            fillOpacity="0.18"
          />
          <text
            x="52"
            y="68.5"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#3AA86A"
            fontWeight="600"
            textAnchor="middle"
          >
            Ven. 28 mars · 20h
          </text>
          <rect
            x="12"
            y="82"
            width="186"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <text
            x="12"
            y="96"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="22"
            fill="#F0EDE6"
            fontWeight="900"
          >
            148
          </text>
          <text
            x="52"
            y="96"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#F0EDE6"
            opacity="0.42"
          >
            / 200 billets
          </text>
          <rect
            x="12"
            y="102"
            width="186"
            height="6"
            rx="3"
            fill="#1E3020"
            opacity="0.8"
          />
          <rect
            x="12"
            y="102"
            width="138"
            height="6"
            rx="3"
            fill="url(#hi-bgreen)"
          />
          <text
            x="12"
            y="120"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.35"
          >
            74% complet · 52 restants
          </text>
          {/* QR code */}
          <rect x="12" y="132" width="44" height="44" rx="5" fill="#0D1A10" />
          {[
            [0, 0],
            [10, 0],
            [20, 0],
            [0, 10],
            [10, 10],
            [0, 20],
            [20, 20],
            [10, 20],
            [20, 10],
          ].map(([dx, dy], i) => (
            <rect
              key={i}
              x={16 + dx}
              y={136 + dy}
              width="7"
              height="7"
              rx="1"
              fill="#3AA86A"
              opacity={0.5 + i * 0.05}
            />
          ))}
          <text
            x="62"
            y="147"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8.5"
            fill="#F0EDE6"
            opacity="0.45"
          >
            Fosse · CHF 38
          </text>
          <text
            x="62"
            y="160"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#F0EDE6"
            opacity="0.35"
          >
            Théâtre, Lausanne
          </text>
          <rect
            x="130"
            y="134"
            width="74"
            height="40"
            rx="7"
            fill="#3AA86A"
            fillOpacity="0.1"
            stroke="#3AA86A"
            strokeWidth="0.5"
            strokeOpacity="0.35"
          />
          <text
            x="167"
            y="150"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#3AA86A"
            opacity="0.7"
            textAnchor="middle"
          >
            SMS envoyé
          </text>
          <text
            x="167"
            y="162"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.55"
            textAnchor="middle"
          >
            "Votre billet
          </text>
          <text
            x="167"
            y="172"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.55"
            textAnchor="middle"
          >
            est prêt !"
          </text>
        </g>

        {/* ════════════════════════════════════════ */}
        {/* CARD 3 — iEat.ch — droite flottante    */}
        {/* ════════════════════════════════════════ */}
        <g
          transform="translate(568, 88) rotate(5, 185, 200)"
          filter="url(#hi-sh)"
        >
          <rect
            width="200"
            height="262"
            rx="14"
            fill="url(#hi-c3)"
            stroke="#288ED2"
            strokeWidth="0.7"
            strokeOpacity="0.35"
          />
          <rect width="200" height="3" rx="2" fill="url(#hi-brust)" />
          <rect
            x="12"
            y="14"
            width="52"
            height="15"
            rx="7"
            fill="#288ED2"
            fillOpacity="0.16"
            stroke="#288ED2"
            strokeWidth="0.5"
            strokeOpacity="0.5"
          />
          <text
            x="38"
            y="25.5"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#288ED2"
            fontWeight="500"
            textAnchor="middle"
          >
            ieat.ch
          </text>
          <rect
            x="136"
            y="14"
            width="52"
            height="15"
            rx="7"
            fill="#288ED2"
            fillOpacity="0.85"
          />
          <text
            x="162"
            y="25.5"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#fff"
            fontWeight="600"
            textAnchor="middle"
          >
            ● LIVE
          </text>
          <text
            x="12"
            y="50"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="12"
            fill="#F0EDE6"
            fontWeight="700"
          >
            Commandes repas
          </text>
          <text
            x="12"
            y="70"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="22"
            fill="#F0EDE6"
            fontWeight="900"
          >
            7
          </text>
          <text
            x="38"
            y="70"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#F0EDE6"
            opacity="0.42"
          >
            en cours · 12 livrées
          </text>
          <rect
            x="12"
            y="78"
            width="176"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          {/* commandes */}
          {[
            {
              y: 86,
              c: "#288ED2",
              title: "#042 — Pizza ×2 · CHF 26",
              sub: "Livraison · 18:42",
              tag: "En route",
              tagC: "#288ED2",
            },
            {
              y: 132,
              c: "#3D7BF5",
              title: "#041 — Salade César · CHF 18",
              sub: "Click & Collect · 18:38",
              tag: "En cuisine",
              tagC: "#D4A843",
            },
            {
              y: 178,
              c: "#3AA86A",
              title: "#040 — Burger + Frites · CHF 22",
              sub: "Livraison · 18:31",
              tag: "Livré ✓",
              tagC: "#3AA86A",
            },
          ].map((cmd, i) => (
            <g key={i}>
              <rect
                x="12"
                y={cmd.y}
                width="176"
                height="40"
                rx="7"
                fill={cmd.c}
                fillOpacity="0.09"
                stroke={cmd.c}
                strokeWidth="0.5"
                strokeOpacity="0.25"
              />
              <circle cx="22" cy={cmd.y + 12} r="4" fill={cmd.c} />
              <text
                x="32"
                y={cmd.y + 14}
                fontFamily="'DM Sans',sans-serif"
                fontSize="8.5"
                fill="#F0EDE6"
                fontWeight="500"
              >
                {cmd.title}
              </text>
              <text
                x="32"
                y={cmd.y + 26}
                fontFamily="'DM Sans',sans-serif"
                fontSize="7.5"
                fill="#F0EDE6"
                opacity="0.4"
              >
                {cmd.sub}
              </text>
              <rect
                x="130"
                y={cmd.y + 4}
                width="50"
                height="14"
                rx="6"
                fill={cmd.tagC}
                fillOpacity="0.75"
              />
              <text
                x="155"
                y={cmd.y + 14.5}
                fontFamily="'DM Mono',monospace"
                fontSize="7"
                fill={cmd.tagC === "#D4A843" ? "#1A1000" : "#fff"}
                textAnchor="middle"
              >
                {cmd.tag}
              </text>
            </g>
          ))}
          <rect
            x="12"
            y="228"
            width="176"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <text
            x="12"
            y="244"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.3"
          >
            Aujourd'hui · CHF 486 · 0% commission
          </text>
          <text
            x="12"
            y="256"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#3D7BF5"
            opacity="0.5"
          >
            Hébergé en Suisse · RGPD
          </text>
        </g>

        {/* ════════════════════════════════════════ */}
        {/* NOTIFICATION — Network — bas centre    */}
        {/* ════════════════════════════════════════ */}
        <g
          transform="translate(420, 476) rotate(-1.5, 148, 36)"
          filter="url(#hi-sh)"
        >
          <rect
            width="296"
            height="68"
            rx="12"
            fill="#161D2B"
            stroke="#3D7BF5"
            strokeWidth="0.7"
            strokeOpacity="0.45"
          />
          <rect
            x="10"
            y="10"
            width="48"
            height="48"
            rx="8"
            fill="#3D7BF5"
            fillOpacity="0.14"
          />
          <text
            x="34"
            y="38"
            fontFamily="sans-serif"
            fontSize="18"
            textAnchor="middle"
          >
            🤝
          </text>
          <text
            x="68"
            y="26"
            fontFamily="'DM Sans',sans-serif"
            fontSize="9"
            fill="#F0EDE6"
            fontWeight="500"
          >
            Nouveau partenariat Kadomi Network
          </text>
          <text
            x="68"
            y="40"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8"
            fill="#F0EDE6"
            opacity="0.45"
          >
            Brasserie du Port + Théâtre de la Ville
          </text>
          <text
            x="68"
            y="53"
            fontFamily="'DM Mono',monospace"
            fontSize="7.5"
            fill="#3AA86A"
          >
            +40 nouveaux clients cette semaine
          </text>
          <rect
            x="254"
            y="10"
            width="30"
            height="13"
            rx="6"
            fill="#3D7BF5"
            fillOpacity="0.22"
          />
          <text
            x="269"
            y="20"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#3D7BF5"
            textAnchor="middle"
          >
            NEW
          </text>
        </g>

        {/* ════════════════════════════════════════ */}
        {/* TÉLÉPHONE — bas gauche flottant         */}
        {/* ════════════════════════════════════════ */}
        <g transform="translate(28, 290) rotate(-7, 92, 185)">
          <rect
            width="168"
            height="330"
            rx="20"
            fill="url(#hi-ph)"
            stroke="#1E2E44"
            strokeWidth="1.5"
          />
          <rect x="54" y="6" width="60" height="8" rx="4" fill="#0A1020" />
          <rect x="7" y="20" width="154" height="304" rx="14" fill="#0D1828" />
          {/* Header */}
          <rect x="7" y="20" width="154" height="38" rx="14" fill="#111E2E" />
          <rect x="7" y="44" width="154" height="14" fill="#111E2E" />
          <text
            x="14"
            y="36"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="10"
            fill="#F0EDE6"
            fontWeight="700"
          >
            kadomi
          </text>
          <text
            x="14"
            y="46"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7"
            fill="#F0EDE6"
            opacity="0.38"
          >
            Tableau de bord
          </text>
          <circle cx="150" cy="36" r="8" fill="#3D7BF5" fillOpacity="0.18" />
          <text
            x="150"
            y="39.5"
            fontFamily="sans-serif"
            fontSize="9"
            fill="#3D7BF5"
            textAnchor="middle"
          >
            🔔
          </text>
          {/* KPI */}
          <rect x="12" y="62" width="68" height="50" rx="7" fill="#1A2640" />
          <text
            x="20"
            y="77"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#F0EDE6"
            opacity="0.4"
          >
            Ventes
          </text>
          <text
            x="20"
            y="94"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="14"
            fill="#F0EDE6"
            fontWeight="700"
          >
            3.8k
          </text>
          <text
            x="20"
            y="105"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#3AA86A"
          >
            ↑ 18%
          </text>
          <rect x="88" y="62" width="68" height="50" rx="7" fill="#1A2018" />
          <text
            x="96"
            y="77"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#F0EDE6"
            opacity="0.4"
          >
            Billets
          </text>
          <text
            x="96"
            y="94"
            fontFamily="'Playfair Display',Georgia,serif"
            fontSize="14"
            fill="#F0EDE6"
            fontWeight="700"
          >
            148
          </text>
          <text
            x="96"
            y="105"
            fontFamily="'DM Mono',monospace"
            fontSize="7"
            fill="#3AA86A"
          >
            74%
          </text>
          {/* Sparklines */}
          <rect
            x="12"
            y="120"
            width="144"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <polyline
            points="12,164 26,158 40,161 54,154 68,156 82,149 96,152 110,145 124,147 144,140"
            fill="none"
            stroke="#3D7BF5"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <polyline
            points="12,172 26,168 40,171 54,165 68,167 82,162 96,164 110,158 124,161 144,155"
            fill="none"
            stroke="#3AA86A"
            strokeWidth="0.8"
            strokeLinecap="round"
            opacity="0.55"
          />
          {/* Commandes */}
          <rect
            x="12"
            y="182"
            width="144"
            height="0.5"
            fill="#F0EDE6"
            opacity="0.08"
          />
          <rect x="12" y="190" width="144" height="30" rx="5" fill="#1A2238" />
          <circle cx="20" cy="205" r="4" fill="#288ED2" />
          <text
            x="30"
            y="202"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.8"
          >
            Pizza ×2 · CHF 26
          </text>
          <text
            x="30"
            y="213"
            fontFamily="'DM Mono',monospace"
            fontSize="6.5"
            fill="#288ED2"
          >
            En route
          </text>
          <rect x="12" y="226" width="144" height="30" rx="5" fill="#1A2820" />
          <circle cx="20" cy="241" r="4" fill="#3AA86A" />
          <text
            x="30"
            y="238"
            fontFamily="'DM Sans',sans-serif"
            fontSize="7.5"
            fill="#F0EDE6"
            opacity="0.8"
          >
            Salade · CHF 18
          </text>
          <text
            x="30"
            y="249"
            fontFamily="'DM Mono',monospace"
            fontSize="6.5"
            fill="#3AA86A"
          >
            Livré ✓
          </text>
          {/* CTA button */}
          <rect x="12" y="266" width="144" height="28" rx="8" fill="#3D7BF5" />
          <text
            x="84"
            y="284"
            fontFamily="'DM Sans',sans-serif"
            fontSize="8.5"
            fill="#fff"
            fontWeight="600"
            textAnchor="middle"
          >
            Voir les ventes →
          </text>
          {/* Nav bar */}
          <rect x="7" y="302" width="154" height="22" fill="#111E2E" />
          <rect
            x="60"
            y="316"
            width="48"
            height="3"
            rx="1.5"
            fill="#F0EDE6"
            opacity="0.15"
          />
          {["🏠", "📊", "🛍️", "⚙️"].map((ic, i) => (
            <text
              key={i}
              x={24 + i * 38}
              y="315"
              fontFamily="sans-serif"
              fontSize="9"
              fill={i === 0 ? "#3D7BF5" : "#F0EDE6"}
              opacity={i === 0 ? 1 : 0.35}
              textAnchor="middle"
            >
              {ic}
            </text>
          ))}
        </g>

        {/* Vignette bords */}
        <rect
          width="780"
          height="580"
          fill="none"
          stroke="#0D1117"
          strokeWidth="50"
          rx="20"
          opacity="0.45"
        />
      </g>
    </svg>
  );
}

/* ─── HERO ───────────────────────────────────────────────────── */
function Hero() {
  const T = useT();
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        minHeight: isMobile ? "auto" : "100vh",
        padding: isMobile ? "5rem 1.5rem 3rem" : "7rem 3rem 4rem",
        background: T.bg,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        transition: "background .4s",
      }}
    >
      {/* Grille animée */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `
          linear-gradient(rgba(61,123,245,.06) 1px, transparent 1px),
          linear-gradient(90deg, rgba(61,123,245,.06) 1px, transparent 1px)
        `,
          backgroundSize: isMobile ? "32px 32px" : "48px 48px",
          animation: "gridMove 10s linear infinite",
          opacity: 0.5,
        }}
      />
      {/* Halo bleu bas-droit */}
      <div
        style={{
          position: "absolute",
          pointerEvents: "none",
          width: isMobile ? "80vw" : "50vw",
          height: isMobile ? "80vw" : "50vw",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(61,123,245,.1) 0%, transparent 70%)",
          right: isMobile ? "-40vw" : "-10vw",
          bottom: isMobile ? "-20%" : "-10%",
        }}
      />
      {/* Halo rouille haut-gauche */}
      <div
        style={{
          position: "absolute",
          pointerEvents: "none",
          width: isMobile ? "60vw" : "30vw",
          height: isMobile ? "60vw" : "30vw",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(40,142,210,.08) 0%, transparent 70%)",
          left: isMobile ? "-30vw" : "-5vw",
          top: "10%",
        }}
      />

      {/* ── LAYOUT 2 COLONNES ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1240,
          marginInline: "auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
          gap: isMobile ? "2.5rem" : "4rem",
          alignItems: "center",
        }}
      >
        {/* ── COLONNE GAUCHE : texte ── */}
        <div>
          {/* Badge */}
          <div
            className="kd-fade-up"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: ".5rem",
              padding: ".4rem 1.125rem",
              borderRadius: 100,
              background: T.bluePale,
              border: `1px solid ${T.blueBorder}`,
              fontSize: isMobile ? ".65rem" : ".7rem",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: T.blue,
              fontFamily: "'DM Mono',monospace",
              marginBottom: isMobile ? "1.5rem" : "2rem",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: T.blue,
                animation: "pulse 2s infinite",
              }}
            />
            🇨🇭 Made in Switzerland · Depuis 2019
          </div>

          {/* H1 */}
          <h1
            className="kd-fade-up kd-delay-1"
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: isMobile
                ? "clamp(2rem,10vw,2.6rem)"
                : "clamp(2.6rem,4.5vw,5.25rem)",
              fontWeight: 900,
              lineHeight: 1.04,
              letterSpacing: "-.03em",
              color: T.cream,
            }}
          >
            Vendez en ligne.
            <br />
            <em style={{ fontStyle: "italic", color: T.rust }}>Grandissez</em>
            <br />
            localement.
          </h1>

          <p
            className="kd-fade-up kd-delay-2"
            style={{
              marginTop: isMobile ? "1.2rem" : "1.5rem",
              fontSize: isMobile ? ".95rem" : "1.0625rem",
              lineHeight: 1.78,
              color: T.creamMid,
              maxWidth: 480,
            }}
          >
            Boutique, billetterie, livraison de repas — trois solutions dans un
            seul écosystème suisse. Sans commission, sans complications, avec un
            réseau de partenaires locaux pour booster vos ventes.
          </p>

          <div
            className="kd-fade-up kd-delay-3"
            style={{
              marginTop: isMobile ? "1.75rem" : "2.25rem",
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <BtnPrimary href="#contact">Demander une démo →</BtnPrimary>
            {!isMobile && (
              <BtnSecondary href="#products">
                Découvrir les modules
              </BtnSecondary>
            )}
          </div>

          <div
            className="kd-fade-up kd-delay-4"
            style={{
              marginTop: "2.5rem",
              display: "flex",
              alignItems: "center",
              gap: ".75rem",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: ".68rem",
                textTransform: "uppercase",
                letterSpacing: ".1em",
                color: T.creamMuted,
                fontFamily: "'DM Mono',monospace",
              }}
            >
              Paiements
            </span>
            {["TWINT", "Visa", "Mastercard", "Apple Pay", "Klarna"].map((p) => (
              <span
                key={p}
                style={{
                  padding: ".25rem .65rem",
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                  background: T.bgCard,
                  fontFamily: "'DM Mono',monospace",
                  fontSize: ".65rem",
                  color: T.creamMuted,
                }}
              >
                {p}
              </span>
            ))}
          </div>
        </div>

        {/* ── COLONNE DROITE : illustration SVG ── */}
        {!isMobile && (
          <div
            className="kd-fade-up kd-delay-2"
            style={{
              position: "relative",
              filter: "drop-shadow(0 32px 64px rgba(0,0,0,.6))",
            }}
          >
            <HeroIllustration />
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── STATS STRIP ────────────────────────────────────────────── */
function StatsStrip() {
  const T = useT();
  const isMobile = useIsMobile();
  const stats = [
    { num: "0", suf: "%", label: "Commission sur vos ventes" },
    { num: "3", suf: "", label: "Solutions en 1 écosystème" },
    { num: "48", suf: "h", label: "Mise en ligne garantie" },
    { num: "100", suf: "%", label: "Hébergé en Suisse · RGPD" },
  ];
  return (
    <div
      style={{
        background: T.statsGrad,
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
      }}
    >
      {stats.map((s, i) => (
        <div
          key={i}
          style={{
            padding: isMobile ? "1.25rem 1rem" : "1.75rem 2rem",
            borderRight: isMobile
              ? i % 2 === 0
                ? "1px solid rgba(255,255,255,.15)"
                : "none"
              : i < 3
                ? "1px solid rgba(255,255,255,.15)"
                : "none",
            borderBottom:
              isMobile && i < 2 ? "1px solid rgba(255,255,255,.15)" : "none",
          }}
        >
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: isMobile ? "1.875rem" : "2.375rem",
              fontWeight: 900,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            {s.num}
            <span
              style={{
                fontSize: isMobile ? "1.25rem" : "1.5rem",
                opacity: 0.7,
              }}
            >
              {s.suf}
            </span>
          </div>
          <div
            style={{
              fontSize: isMobile ? ".7rem" : ".8rem",
              color: "rgba(255,255,255,.7)",
              marginTop: ".3rem",
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── PAIN POINTS ────────────────────────────────────────────── */
const PAINS = [
  {
    icon: <MoneyOffIcon />,
    title: "Vendre en ligne coûte trop cher",
    body: "Les solutions existantes sont trop techniques ou bourrées de frais cachés et de commissions.",
  },
  {
    icon: <AccountBalanceIcon />,
    title: "Dépendance aux grandes plateformes",
    body: "Amazon, Shopify, Eventbrite — elles prennent des commissions élevées et vous laissent gérer seul.",
  },
  {
    icon: <AccessTimeIcon />,
    title: "Trop de temps perdu en technique",
    body: "La maintenance, les mises à jour, la sécurité — autant de choses qui vous éloignent de votre métier.",
  },
  {
    icon: <CampaignIcon />,
    title: "Manque de visibilité locale",
    body: "Budget marketing limité, difficulté à attirer de nouveaux clients dans votre zone géographique.",
  },
  {
    icon: <LoopIcon />,
    title: "Fidélisation difficile",
    body: "Vos clients achètent une fois et ne reviennent pas. Vous manquez d'outils pour entretenir la relation.",
  },
];

function PainCard({ icon, title, body, highlight, delay }) {
  const T = useT();
  const ref = useReveal();
  const [h, setH] = useState(false);
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${delay}`}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: highlight
          ? `linear-gradient(135deg,${T.rust},${T.rustHov})`
          : h
            ? T.bgLight
            : T.bgCard,
        border: `1px solid ${highlight ? "transparent" : h ? T.borderMid : T.border}`,
        borderRadius: 16,
        padding: "1.875rem",
        boxShadow: h && !highlight ? `0 8px 32px ${T.shadow}` : "none",
        transform: h ? "translateY(-3px)" : "none",
        transition: "all .25s",
      }}
    >
      <div
        style={{
          fontSize: "2rem",
          marginBottom: ".875rem",
          color: highlight ? "#fff" : T.rust,
        }}
      >
        {icon}
      </div>
      <h3
        style={{
          fontSize: ".9375rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: T.cream,
          marginBottom: ".5rem",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontSize: ".8375rem",
          lineHeight: 1.65,
          color: highlight ? "rgba(255,255,255,.8)" : T.creamMid,
        }}
      >
        {body}
      </p>
    </div>
  );
}

function PainSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        background: T.bgAlt,
        padding: isMobile ? "4rem 1.5rem" : "6rem 3rem",
      }}
    >
      <div style={{ maxWidth: 1100, marginInline: "auto" }}>
        <div ref={ref} className="kd-reveal">
          <Label>Le problème</Label>
          <Title>Vous reconnaissez ces difficultés ?</Title>
          <Sub>
            Gérer une activité, c'est déjà assez compliqué. Vous n'avez pas
            besoin de problèmes techniques en plus.
          </Sub>
        </div>
        <div
          style={{
            marginTop: "3rem",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fit,minmax(260px,1fr))",
            gap: "1rem",
          }}
        >
          {PAINS.map((p, i) => (
            <PainCard key={i} {...p} delay={(i % 3) + 1} />
          ))}
          <PainCard
            icon={<CheckCircleIcon />}
            highlight
            delay={1}
            title="Kadomi résout tout ça"
            body="Une plateforme tout-en-un pensée pour les commerces suisses. Support humain, en français, toujours à vos côtés."
          />
        </div>
      </div>
    </section>
  );
}

/* ─── PRODUCTS ───────────────────────────────────────────────── */
const getProducts = (T) => [
  {
    tag: "kadomi.ch",
    emoji: "🛍️",
    accentColor: T.rust,
    accentPale: T.rustPale,
    accentBorder: T.rustBorder,
    barGradient: `linear-gradient(90deg,${T.rust},#E88050)`,
    name: "Votre boutique en ligne",
    desc: "Vendez vos produits physiques ou digitaux — vêtements, cours, goodies, artisanat — en quelques clics.",
    features: [
      "Gestion automatique des stocks",
      "Paiements sécurisés (CB, TWINT, Apple Pay)",
      "Catalogue produits illimité",
      "Emails automatiques de confirmation",
    ],
  },
  {
    tag: "myticketing.ch",
    emoji: "🎟️",
    accentColor: "#3AA86A",
    accentPale: "rgba(58,168,106,.14)",
    accentBorder: "rgba(58,168,106,.3)",
    barGradient: "linear-gradient(90deg,#2D8050,#3AA86A)",
    name: "Billetterie événementielle",
    desc: "Vendez des billets pour concerts, théâtre, festivals et conférences avec QR codes et contrôle d'accès.",
    features: [
      "Billets PDF avec QR code automatique",
      "Contrôle d'accès en temps réel",
      "Emails de confirmation automatiques",
      "SMS optionnels (0.04 CHF/SMS)",
    ],
  },
  {
    tag: "ieat.ch",
    emoji: "🍽️",
    accentColor: T.blue,
    accentPale: T.bluePale,
    accentBorder: T.blueBorder,
    barGradient: `linear-gradient(90deg,${T.blue},#6B9FFA)`,
    name: "Commande & livraison",
    desc: "Permettez à vos clients de commander en ligne — livraison ou click & collect. Idéal pour restaurants et food trucks.",
    features: [
      "Menu en ligne personnalisable",
      "Commandes et notifications en temps réel",
      "Click & collect ou livraison",
      "Rappels SMS optionnels",
    ],
  },
];

function ProductCard({ product, delay }) {
  const T = useT();
  const ref = useReveal();
  const [h, setH] = useState(false);
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${delay}`}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: h ? T.bgLight : T.bgCard,
        border: `1px solid ${h ? product.accentBorder : T.border}`,
        borderRadius: 20,
        padding: "2.25rem",
        position: "relative",
        overflow: "hidden",
        transform: h ? "translateY(-5px)" : "none",
        boxShadow: h ? `0 16px 48px ${T.shadowMd}` : `0 2px 12px ${T.shadow}`,
        transition: "all .28s",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: product.barGradient,
          opacity: h ? 1 : 0.5,
          transition: "opacity .28s",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ".625rem",
          marginBottom: "1.375rem",
        }}
      >
        <span style={{ fontSize: "1.5rem" }}>{product.emoji}</span>
        <span
          style={{
            padding: ".22rem .75rem",
            borderRadius: 100,
            background: product.accentPale,
            border: `1px solid ${product.accentBorder}`,
            color: product.accentColor,
            fontFamily: "'DM Mono',monospace",
            fontSize: ".68rem",
            letterSpacing: ".08em",
            fontWeight: 500,
          }}
        >
          {product.tag}
        </span>
      </div>
      <h3
        style={{
          fontFamily: "'Playfair Display',serif",
          fontSize: "1.5rem",
          fontWeight: 700,
          color: T.cream,
          marginBottom: ".6rem",
          lineHeight: 1.25,
        }}
      >
        {product.name}
      </h3>
      <p
        style={{
          fontSize: ".875rem",
          lineHeight: 1.72,
          color: T.creamMid,
          marginBottom: "1.75rem",
        }}
      >
        {product.desc}
      </p>
      <ul
        style={{
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: ".6rem",
        }}
      >
        {product.features.map((f, i) => (
          <li
            key={i}
            style={{
              fontSize: ".8125rem",
              color: T.creamMid,
              display: "flex",
              alignItems: "flex-start",
              gap: ".625rem",
            }}
          >
            <span
              style={{
                flexShrink: 0,
                marginTop: "2px",
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: product.accentPale,
                border: `1px solid ${product.accentBorder}`,
                display: "grid",
                placeItems: "center",
                color: product.accentColor,
                fontSize: ".55rem",
                fontWeight: 700,
              }}
            >
              ✓
            </span>
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductsSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      id="products"
      style={{
        background: T.bg,
        padding: isMobile ? "4rem 1.5rem" : "6.5rem 3rem",
      }}
    >
      <div style={{ maxWidth: 1100, marginInline: "auto" }}>
        <div ref={ref} className="kd-reveal" style={{ maxWidth: 640 }}>
          <Label>3 produits · 1 écosystème</Label>
          <Title>Choisissez ce dont vous avez besoin</Title>
          <Sub>
            Prenez uniquement le module qui correspond à votre activité.
            Combinez et évoluez à votre rythme, sans engagement.
          </Sub>
        </div>
        <div
          style={{
            marginTop: "3.5rem",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
            gap: "1.5rem",
          }}
        >
          {getProducts(T).map((p, i) => (
            <ProductCard key={i} product={p} delay={i + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── NETWORK ────────────────────────────────────────────────── */
const NET_STEPS = [
  {
    n: "01",
    title: "Trouvez un partenaire",
    body: "Un théâtre s'associe à un restaurant, une boutique avec un événement. La plateforme facilite la mise en relation.",
  },
  {
    n: "02",
    title: "Créez une offre croisée",
    body: '"10% de réduction au restaurant après le spectacle" — visibilité gratuite et ciblée pour les deux partenaires.',
  },
  {
    n: "03",
    title: "Promouvez-vous mutuellement",
    body: "Sur vos réseaux sociaux, vos billets PDF, vos emails de confirmation et vos flyers événementiels.",
  },
];

function NetStepItem({ step, index }) {
  const T = useT();
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${index + 1}`}
      style={{ display: "flex", gap: "1.125rem", alignItems: "flex-start" }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: "50%",
          background: `linear-gradient(135deg,${T.blue},#2558C8)`,
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontFamily: "'DM Mono',monospace",
          fontSize: ".72rem",
          fontWeight: 500,
          boxShadow: "0 4px 14px rgba(61,123,245,.4)",
        }}
      >
        {step.n}
      </div>
      <div>
        <h4
          style={{
            fontWeight: 600,
            fontSize: ".9375rem",
            color: T.cream,
            marginBottom: ".3rem",
          }}
        >
          {step.title}
        </h4>
        <p style={{ fontSize: ".85rem", color: T.creamMid, lineHeight: 1.65 }}>
          {step.body}
        </p>
      </div>
    </div>
  );
}

function PartnerCard({ card, index }) {
  const T = useT();
  const ref = useReveal();
  const [h, setH] = useState(false);
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${index + 1}`}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        gridColumn: card.span === 2 ? "span 2" : undefined,
        background: h ? T.bgLight : T.bgCard,
        border: `1px solid ${T.border}`,
        borderTop: `3px solid ${card.color}`,
        borderRadius: 16,
        padding: "1.5rem",
        transform: h ? "scale(1.025)" : "scale(1)",
        boxShadow: h ? `0 8px 28px ${T.shadow}` : "none",
        transition: "all .25s",
      }}
    >
      <div style={{ fontSize: "1.375rem", marginBottom: ".625rem" }}>
        {card.icon}
      </div>
      <h4
        style={{
          fontWeight: 600,
          fontSize: ".875rem",
          color: T.cream,
          marginBottom: ".3rem",
        }}
      >
        {card.title}
      </h4>
      <p style={{ fontSize: ".78rem", color: T.creamMid, lineHeight: 1.55 }}>
        {card.desc}
      </p>
    </div>
  );
}

function NetworkSection() {
  const T = useT();
  const ref = useReveal();
  const ref2 = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      id="network"
      style={{
        background: T.bgAlt,
        padding: isMobile ? "4rem 1.5rem" : "6.5rem 3rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            fontFamily: "'Playfair Display',serif",
            fontSize: "18vw",
            fontWeight: 900,
            lineHeight: 1,
            color: T.dark ? "rgba(61,123,245,.04)" : "rgba(40,142,210,.04)",
            bottom: "-2%",
            right: "-2%",
            pointerEvents: "none",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          NETWORK
        </div>
      )}

      <div
        style={{
          maxWidth: 1100,
          marginInline: "auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? "3rem" : "5rem",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div>
          <div ref={ref} className="kd-reveal">
            <Label>Kadomi Network</Label>
            <Title>Votre différenciateur unique</Title>
            <Sub>
              Connectez-vous avec d'autres commerces locaux pour échanger de la
              visibilité et attirer de nouveaux clients — sans budget marketing.
            </Sub>
          </div>
          <div
            style={{
              marginTop: "2.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.625rem",
            }}
          >
            {NET_STEPS.map((s, i) => (
              <NetStepItem key={i} step={s} index={i} />
            ))}
          </div>
          <div
            ref={ref2}
            className="kd-reveal"
            style={{
              marginTop: "2rem",
              display: "flex",
              gap: ".625rem",
              flexWrap: "wrap",
            }}
          >
            {[
              "Réseaux sociaux",
              "Emails de confirmation",
              "Billets PDF",
              "Flyers",
            ].map((ch) => (
              <span
                key={ch}
                style={{
                  padding: ".35rem .875rem",
                  borderRadius: 100,
                  background: T.bluePale,
                  border: `1px solid ${T.blueBorder}`,
                  color: T.blue,
                  fontSize: ".75rem",
                  fontWeight: 500,
                }}
              >
                📍 {ch}
              </span>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
          }}
        >
          {[
            {
              icon: "🎭",
              title: "Théâtre de la Ville",
              desc: "Affiche l'offre restaurant dans ses billets → 40 nouveaux couverts/semaine",
              span: 2,
              color: T.rust,
            },
            {
              icon: "🍽️",
              title: "Brasserie du Port",
              desc: "-15% aux détenteurs de billets → fidélisation post-spectacle",
              span: 1,
              color: T.blue,
            },
            {
              icon: "📈",
              title: "Résultat concret",
              desc: "Nouveaux clients sans pub coûteuse, réseau professionnel local solide",
              span: 1,
              color: "#3AA86A",
            },
          ].map((c, i) => (
            <PartnerCard key={i} card={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── VS TABLE ───────────────────────────────────────────────── */
const VS_ROWS = [
  {
    feat: "0% commission sur ventes",
    k: true,
    sh: "0.5–2%",
    wx: "Variable",
    woo: true,
    inf: true,
  },
  {
    feat: "Support francophone",
    k: "Dédié",
    sh: "Anglais",
    wx: "Générique",
    woo: "Communauté",
    inf: true,
  },
  {
    feat: "Billetterie intégrée",
    k: "Natif",
    sh: "Plugin",
    wx: "Plugin",
    woo: "Plugin",
    inf: false,
  },
  {
    feat: "Food ordering",
    k: "Natif",
    sh: false,
    wx: false,
    woo: "Plugin",
    inf: false,
  },
  {
    feat: "Réseau de partenariats",
    k: "Unique",
    sh: false,
    wx: false,
    woo: false,
    inf: false,
  },
  {
    feat: "Hébergement Suisse · RGPD",
    k: true,
    sh: "USA",
    wx: "USA",
    woo: "Selon host",
    inf: true,
  },
];

function VsSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        background: T.bg,
        padding: isMobile ? "4rem 1.5rem" : "6.5rem 3rem",
      }}
    >
      <div style={{ maxWidth: 980, marginInline: "auto" }}>
        <div ref={ref} className="kd-reveal">
          <Label>Comparatif</Label>
          <Title>Pourquoi Kadomi plutôt que les autres ?</Title>
          <Sub>
            Les grandes plateformes prennent des commissions et vous laissent
            seul. On fait différemment.
          </Sub>
        </div>
        <div
          style={{
            marginTop: "3rem",
            overflowX: isMobile ? "auto" : "visible",
          }}
        >
          <div
            ref={useReveal()}
            className="kd-reveal"
            style={{
              minWidth: isMobile ? "800px" : "auto",
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 20,
              overflow: "hidden",
              boxShadow: `0 4px 24px ${T.shadow}`,
            }}
          >
            {/* header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
              }}
            >
              <div
                style={{
                  padding: "1rem 1.25rem",
                  background: T.bgAlt,
                  fontSize: ".72rem",
                  fontFamily: "'DM Mono',monospace",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  color: T.creamMuted,
                  borderRight: `1px solid ${T.border}`,
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                Fonctionnalité
              </div>
              {["Kadomi", "Shopify", "Wix", "WooCommerce", "Infomaniak"].map(
                (c, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "1rem 1.25rem",
                      fontFamily: "'DM Mono',monospace",
                      fontSize: ".72rem",
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: i === 0 ? "#fff" : T.creamMuted,
                      background:
                        i === 0
                          ? `linear-gradient(135deg,${T.blue},#2558C8)`
                          : T.bgAlt,
                      borderRight: i < 4 ? `1px solid ${T.border}` : "none",
                      borderBottom: `1px solid ${T.border}`,
                      fontWeight: i === 0 ? 600 : 400,
                    }}
                  >
                    {c}
                  </div>
                )
              )}
            </div>
            {VS_ROWS.map((row, ri) => (
              <div
                key={ri}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
                  borderBottom:
                    ri < VS_ROWS.length - 1 ? `1px solid ${T.border}` : "none",
                  background: ri % 2 === 1 ? T.bgAlt : T.bgCard,
                }}
              >
                <div
                  style={{
                    padding: "1rem 1.25rem",
                    fontSize: ".8375rem",
                    fontWeight: 500,
                    color: T.cream,
                    borderRight: `1px solid ${T.border}`,
                  }}
                >
                  {row.feat}
                </div>
                <div
                  style={{
                    padding: "1rem 1.25rem",
                    fontSize: ".8rem",
                    color: T.vsKadTxt,
                    fontWeight: 600,
                    background: T.vsKadBg,
                    borderRight: `1px solid ${T.border}`,
                  }}
                >
                  {row.k === true ? "✓ Oui" : `✓ ${row.k}`}
                </div>
                {[row.sh, row.wx, row.woo, row.inf].map((v, ci) => (
                  <div
                    key={ci}
                    style={{
                      padding: "1rem 1.25rem",
                      fontSize: ".8rem",
                      color: v === false ? "rgba(240,237,230,.2)" : T.creamMid,
                      borderRight: ci < 3 ? `1px solid ${T.border}` : "none",
                    }}
                  >
                    {v === true ? (
                      <span style={{ color: T.vsKadTxt }}>✓ Oui</span>
                    ) : v === false ? (
                      <span style={{ color: "rgba(240,237,230,.2)" }}>
                        ✗ Non
                      </span>
                    ) : (
                      <span style={{ color: T.creamMuted }}>✗ {v}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PRICING ────────────────────────────────────────────────── */
const PLANS = [
  {
    name: "Essentiel",
    amount: "69",
    featured: false,
    for: "Petites structures, associations, artisans",
    features: [
      [true, "1 module au choix (Boutique / Billetterie / Food)"],
      [true, "Paiements sécurisés (CB, TWINT, Apple Pay)"],
      [true, "Emails automatiques illimités"],
      [true, "Catalogue produits illimité"],
      [true, "Hébergement sécurisé inclus"],
      [true, "Support par email (48h)"],
      [false, "SMS offerts"],
      [false, "Accès Kadomi Network"],
    ],
  },
  {
    name: "Pro",
    amount: "129",
    featured: true,
    for: "Théâtres, restaurants, commerces établis",
    features: [
      [true, "Jusqu'à 2 modules"],
      [true, "Tout de l'offre Essentiel"],
      [true, "100 SMS offerts/mois"],
      [true, "Statistiques et rapports avancés"],
      [true, "Export données clients (CSV)"],
      [true, "Support téléphonique prioritaire"],
      [true, "Formation et accompagnement inclus"],
      [false, "Accès Kadomi Network"],
    ],
  },
  {
    name: "Network+",
    amount: "179",
    featured: false,
    for: "PME, groupes d'événements, franchises",
    features: [
      [true, "Tous les modules illimités"],
      [true, "Tout de l'offre Pro"],
      [true, "250 SMS offerts/mois"],
      [true, "Accès exclusif Kadomi Network"],
      [true, "Partenariats et visibilité croisée"],
      [true, "Outils marketing collaboratifs"],
      [true, "Support prioritaire dédié (24h)"],
      [true, "API avancée disponible"],
    ],
  },
];

function PlanCard({ plan, delay }) {
  const T = useT();
  const ref = useReveal();
  const [h, setH] = useState(false);
  const isFeat = plan.featured;
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${delay}`}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: isFeat ? T.planFeatBg : h ? T.bgLight : T.bgCard,
        border: `1px solid ${isFeat ? "transparent" : h ? T.blueBorder : T.border}`,
        borderRadius: 20,
        padding: "2.25rem",
        position: "relative",
        transform: h || isFeat ? "translateY(-5px)" : "none",
        boxShadow: isFeat
          ? "0 20px 56px rgba(61,123,245,.38)"
          : h
            ? `0 12px 40px ${T.shadowMd}`
            : `0 2px 12px ${T.shadow}`,
        transition: "all .28s, background .4s",
        zIndex: isFeat ? 2 : 1,
      }}
    >
      {isFeat && (
        <div
          style={{
            position: "absolute",
            top: -14,
            left: "50%",
            transform: "translateX(-50%)",
            background: T.planFeatBadge,
            color: "#fff",
            padding: ".25rem 1rem",
            borderRadius: 100,
            fontSize: ".68rem",
            fontWeight: 700,
            letterSpacing: ".06em",
            whiteSpace: "nowrap",
          }}
        >
          ⭐ Plus populaire
        </div>
      )}
      <div
        style={{
          fontFamily: "'DM Mono',monospace",
          fontSize: ".68rem",
          letterSpacing: ".12em",
          textTransform: "uppercase",
          color: isFeat ? "rgba(255,255,255,.6)" : T.creamMuted,
          marginBottom: ".625rem",
        }}
      >
        {plan.name}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: ".25rem",
          marginBottom: ".375rem",
        }}
      >
        <span
          style={{
            fontSize: "1rem",
            color: isFeat ? "rgba(255,255,255,.6)" : T.creamMuted,
          }}
        >
          CHF
        </span>
        <span
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "3rem",
            fontWeight: 900,
            lineHeight: 1,
            color: "#fff",
          }}
        >
          {plan.amount}
        </span>
        <span
          style={{
            fontSize: ".875rem",
            color: isFeat ? "rgba(255,255,255,.5)" : T.creamMuted,
          }}
        >
          /mois
        </span>
      </div>
      <p
        style={{
          fontSize: ".78rem",
          lineHeight: 1.55,
          marginBottom: "1.625rem",
          color: isFeat ? "rgba(255,255,255,.65)" : T.creamMuted,
        }}
      >
        💡 {plan.for}
      </p>
      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${isFeat ? "rgba(255,255,255,.2)" : T.border}`,
          marginBottom: "1.5rem",
        }}
      />
      <ul
        style={{
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: ".625rem",
        }}
      >
        {plan.features.map(([ok, label], i) => (
          <li
            key={i}
            style={{
              display: "flex",
              gap: ".5rem",
              alignItems: "flex-start",
              fontSize: ".8rem",
              lineHeight: 1.5,
              color: ok
                ? isFeat
                  ? "rgba(255,255,255,.88)"
                  : T.creamMid
                : isFeat
                  ? "rgba(255,255,255,.3)"
                  : "rgba(240,237,230,.22)",
            }}
          >
            <span
              style={{
                flexShrink: 0,
                marginTop: "1px",
                fontWeight: 700,
                fontSize: ".8rem",
                color: ok
                  ? isFeat
                    ? "rgba(255,255,255,.75)"
                    : "#3AA86A"
                  : "transparent",
              }}
            >
              {ok ? "✓" : "–"}
            </span>
            {label}
          </li>
        ))}
      </ul>
      <a
        href="#contact"
        style={{
          display: "block",
          textAlign: "center",
          marginTop: "2rem",
          padding: ".825rem 1.5rem",
          borderRadius: 100,
          fontWeight: 600,
          fontSize: ".875rem",
          textDecoration: "none",
          transition: "all .2s",
          background: isFeat ? T.planFeatCta : T.blue,
          color: isFeat ? T.planFeatCtaTxt : "#fff",
          boxShadow: isFeat
            ? "0 4px 14px rgba(0,0,0,.2)"
            : "0 2px 10px rgba(61,123,245,.35)",
        }}
      >
        Démarrer →
      </a>
    </div>
  );
}

function PricingSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      id="pricing"
      style={{
        background: T.bgAlt,
        padding: isMobile ? "4rem 1.5rem" : "6.5rem 3rem",
      }}
    >
      <div style={{ maxWidth: 1100, marginInline: "auto" }}>
        <div
          ref={ref}
          className="kd-reveal"
          style={{ textAlign: "center", maxWidth: 540, marginInline: "auto" }}
        >
          <Label>Tarifs</Label>
          <Title center>Simple, transparent, sans engagement</Title>
          <Sub center>
            Pas de commission. Pas de frais cachés. Un abonnement mensuel clair,
            résiliable à tout moment.
          </Sub>
        </div>
        <div
          style={{
            marginTop: "3.5rem",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
            gap: "1.25rem",
            alignItems: "start",
          }}
        >
          {PLANS.map((p, i) => (
            <PlanCard key={i} plan={p} delay={i + 1} />
          ))}
        </div>
        <p
          ref={useReveal()}
          className="kd-reveal"
          style={{
            marginTop: "2rem",
            textAlign: "center",
            fontSize: ".8rem",
            color: T.creamMuted,
            lineHeight: 1.9,
          }}
        >
          Sans engagement · Résiliable à tout moment
          <br />
          Frais Stripe : 2.9% + 0.30 CHF / transaction · Kadomi ne prend aucune
          commission supplémentaire
        </p>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ───────────────────────────────────────────── */
const getTestimonials = (T) => [
  {
    initials: "SM",
    avatarBg: T.rust,
    name: "Sophie Martin",
    role: "Fondatrice — Talent-Avenir",
    text: "Kadomi a révolutionné notre façon de vendre nos formations en ligne. La plateforme est intuitive, le support est réactif, et surtout, nous ne payons aucune commission sur nos ventes ! En quelques semaines, nous avons augmenté notre visibilité locale grâce au réseau de partenaires.",
  },
  {
    initials: "GC",
    avatarBg: T.blue,
    name: "Grégory Chiaradia",
    role: "Directrice — Centre Linguistique Lausanne",
    text: "Grâce à MyTicketing.ch, nous gérons nos événements et ateliers en toute simplicité. Le système de billetterie est fluide, les participants paient avec TWINT, et nous avons réduit nos coûts de 40% par rapport à notre ancienne solution. Le réseau Kadomi nous apporte même de nouveaux clients !",
  },
];

function TestimonialItem({ testimonial, index }) {
  const T = useT();
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`kd-reveal kd-d${index + 1}`}
      style={{
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        padding: "2rem",
        position: "relative",
        boxShadow: `0 2px 12px ${T.shadow}`,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "1.25rem",
          right: "1.75rem",
          fontFamily: "'Playfair Display',serif",
          fontSize: "5rem",
          lineHeight: 1,
          color: T.tesiQuote,
          userSelect: "none",
        }}
      >
        "
      </div>
      <div style={{ display: "flex", gap: 3, marginBottom: "1rem" }}>
        {[...Array(5)].map((_, j) => (
          <span key={j} style={{ color: T.gold, fontSize: ".9rem" }}>
            ★
          </span>
        ))}
      </div>
      <p
        style={{
          fontSize: ".9rem",
          lineHeight: 1.8,
          color: T.creamMid,
          fontStyle: "italic",
          marginBottom: "1.625rem",
        }}
      >
        "{testimonial.text}"
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            background: testimonial.avatarBg,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontFamily: "'DM Mono',monospace",
            fontSize: ".8rem",
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {testimonial.initials}
        </div>
        <div>
          <div
            style={{ fontWeight: 600, fontSize: ".8125rem", color: T.cream }}
          >
            {testimonial.name}
          </div>
          <div style={{ fontSize: ".72rem", color: T.creamMuted }}>
            {testimonial.role}
          </div>
        </div>
      </div>
    </div>
  );
}

function TestiSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        background: T.bg,
        padding: isMobile ? "4rem 1.5rem" : "6rem 3rem",
      }}
    >
      <div style={{ maxWidth: 1100, marginInline: "auto" }}>
        <div
          ref={ref}
          className="kd-reveal"
          style={{ textAlign: "center", maxWidth: 480, marginInline: "auto" }}
        >
          <Label>Témoignages</Label>
          <Title center>Ils nous font confiance</Title>
        </div>
        <div
          style={{
            marginTop: "3.5rem",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)",
            gap: "1.5rem",
          }}
        >
          {getTestimonials(T).map((t, i) => (
            <TestimonialItem key={i} testimonial={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ────────────────────────────────────────────────────── */
function CtaSection() {
  const T = useT();
  const ref = useReveal();
  const isMobile = useIsMobile();
  return (
    <section
      id="contact"
      style={{
        background: T.bgDark,
        padding: isMobile ? "5rem 1.5rem" : "8rem 3rem",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: T.dark
            ? `radial-gradient(ellipse 65% 65% at 50% 50%, rgba(61,123,245,.12) 0%, transparent 70%)`
            : "radial-gradient(ellipse 65% 65% at 50% 50%, rgba(40,142,210,.1) 0%, transparent 70%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.04,
          pointerEvents: "none",
          backgroundImage: `
          linear-gradient(rgba(61,123,245,.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(61,123,245,.4) 1px, transparent 1px)
        `,
          backgroundSize: "48px 48px",
        }}
      />
      <div
        ref={ref}
        className="kd-reveal"
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 640,
          marginInline: "auto",
        }}
      >
        <h2
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: isMobile ? "2.5rem" : "clamp(2.5rem,5vw,4rem)",
            fontWeight: 900,
            lineHeight: 1.1,
            letterSpacing: "-.025em",
            color: T.cream,
            marginBottom: "1.25rem",
          }}
        >
          Prêt à démarrer ?
        </h2>
        <p
          style={{
            fontSize: isMobile ? ".95rem" : "1.0625rem",
            lineHeight: 1.78,
            color: T.creamMid,
            marginBottom: "2.5rem",
          }}
        >
          Découvrez comment Kadomi peut transformer votre activité.
          Démonstration gratuite, sans engagement — nous vous répondons en 24h.
        </p>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <BtnPrimary href="mailto:info@kadomi.shop">
            Demander une démo →
          </BtnPrimary>
          <BtnSecondary href="tel:+41796091114">
            📞 +41 79 609 11 14
          </BtnSecondary>
        </div>
        <p
          style={{
            marginTop: "2rem",
            fontSize: ".875rem",
            color: T.creamMuted,
          }}
        >
          ✉ info@kadomi.shop · Réponse garantie en 24h
        </p>
      </div>
    </section>
  );
}

/* ─── FOOTER ─────────────────────────────────────────────────── */
function Footer() {
  const T = useT();
  const isMobile = useIsMobile();
  const cols = [
    {
      title: "Produits",
      links: ["Kadomi.ch", "MyTicketing.ch", "iEat.ch", "Kadomi Network"],
    },
    {
      title: "Informations",
      links: ["Tarifs", "FAQ", "Demander une démo", "Connexion clients"],
    },
    { title: "Contact", links: ["info@kadomi.shop", "+41 79 609 11 14"] },
  ];
  return (
    <footer
      style={{
        background: T.footerBg,
        borderTop: `1px solid ${T.footerBrd}`,
        padding: isMobile ? "3rem 1.5rem 2rem" : "3.5rem 3rem 2rem",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          marginInline: "auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr",
          gap: isMobile ? "2.5rem" : "3rem",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontWeight: 900,
              fontSize: "1.25rem",
              color: T.cream,
            }}
          >
            kadomi<span style={{ color: T.rust }}>.</span>
            <span style={{ color: T.creamMuted, fontWeight: 700 }}>shop</span>
          </div>
          <p
            style={{
              marginTop: ".875rem",
              fontSize: ".8125rem",
              color: T.creamMuted,
              lineHeight: 1.75,
              maxWidth: 280,
            }}
          >
            Votre partenaire suisse pour vendre en ligne et développer votre
            activité grâce aux partenariats locaux.
          </p>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: ".4rem",
              marginTop: "1.125rem",
              background: T.rustPale,
              color: "#F0A080",
              padding: ".25rem .75rem",
              borderRadius: 100,
              fontFamily: "'DM Mono',monospace",
              fontSize: ".65rem",
              letterSpacing: ".08em",
            }}
          >
            🇨🇭 Made in Switzerland
          </span>
        </div>
        {cols.map((col, i) => (
          <div key={i}>
            <h4
              style={{
                fontFamily: "'DM Mono',monospace",
                fontSize: ".67rem",
                letterSpacing: ".12em",
                textTransform: "uppercase",
                color: T.creamMuted,
                marginBottom: "1.25rem",
              }}
            >
              {col.title}
            </h4>
            <ul
              style={{
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: ".625rem",
              }}
            >
              {col.links.map((l, j) => (
                <li key={j}>
                  <a
                    href="#"
                    className="kd-footer-link"
                    style={{
                      fontSize: ".875rem",
                      color: T.creamMuted,
                      textDecoration: "none",
                      transition: "color .18s",
                    }}
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div
        style={{
          maxWidth: 1200,
          marginInline: "auto",
          marginTop: "3rem",
          paddingTop: "1.5rem",
          borderTop: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: ".72rem",
          color: T.footerTxt,
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <span>© 2019–2026 Kadomi.shop — Tous droits réservés</span>
        <span>Hébergé en Suisse · Conforme RGPD · Support en français</span>
      </div>
    </footer>
  );
}

/* ─── ROOT ───────────────────────────────────────────────────── */
export default function KadomiLanding() {
  const [isDark, setIsDark] = useState(false);
  const T = getTheme(isDark);

  useEffect(() => {
    injectFonts();
    injectGlobalStyles();
  }, []);

  return (
    <ThemeCtx.Provider value={T}>
      <div
        style={{
          fontFamily: "'DM Sans',sans-serif",
          background: T.bg,
          color: T.cream,
          transition: "background .4s, color .4s",
        }}
      >
        <Nav isDark={isDark} onToggle={() => setIsDark((d) => !d)} />
        <Hero />
        <StatsStrip />
        <PainSection />
        {/* <ProductsSection /> */}
        <NetworkSection />
        <VsSection />
        <PricingSection />
        <TestiSection />
        <CtaSection />
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
